import { createActionsHook, createHook, createStateHook, createStore } from 'react-sweet-state';

import type { ConversationAction, CustomFollowUpContext } from '../../common/types';

type ConversationActionStore = {
	actionFilter?: (actions: ConversationAction[]) => ConversationAction[];
	actions: ConversationAction[];
	input?: string;
	activeAction?: ConversationAction;
	buttonClickedTimestamp?: number;

	/*
	 * Jira Issue View does not use the provider pattern yet, so we need to skip the provider action fetching.
	 * When Jira Issue View is migrated to set actions using provider pattern, we can remove this store.
	 */
	jiraIssueViewActions?: ConversationAction[];
};

const store = createStore({
	initialState: {
		actionFilter: undefined,
		actions: [],
		jiraIssueViewActions: undefined,
		input: '',
		activeAction: undefined,
		buttonClickedTimestamp: undefined,
	} as ConversationActionStore,
	actions: {
		setConversationActions:
			(actions: ConversationAction[]) =>
			({ setState }) => {
				setState({
					actions: actions,
				});
			},
		/*
		 * Jira Issue View does not use the provider pattern yet, so we need to skip the provider action fetching.
		 * When Jira Issue View is migrated to set actions using provider pattern, we can remove this setter.
		 */
		setJiraIssueViewActions:
			(actions: ConversationAction[]) =>
			({ setState }) => {
				setState({
					jiraIssueViewActions: actions,
				});
			},
		setInput:
			(input: string) =>
			({ setState }) => {
				setState({
					input: input,
				});
			},
		setActionFilter:
			(actionFilter: (actions: ConversationAction[]) => ConversationAction[]) =>
			({ setState }) => {
				setState({
					actionFilter: actionFilter,
				});
			},
		/*
		 * Do not use this setter directly. Use setActiveAction from useActiveAction hook in conversation-assistant instead.
		 */
		setActiveAction_UNSAFE:
			(activeAction: ConversationAction | undefined) =>
			({ setState }) => {
				setState({
					activeAction: activeAction,
				});
			},
		setActiveActionFollowUpContext:
			(customFollowUpContext: CustomFollowUpContext | undefined) =>
			({ getState, setState }) => {
				const activeAction = getState().activeAction;

				if (!activeAction || !('customFollowUpContext' in activeAction)) {
					return;
				}

				setState({
					activeAction: {
						...activeAction,
						customFollowUpContext: customFollowUpContext,
					},
				});
			},
		setButtonClickedTimestamp:
			(buttonClickedTimestamp: number) =>
			({ setState }) => {
				setState({
					buttonClickedTimestamp: buttonClickedTimestamp,
				});
			},
		getConversationActions:
			() =>
			({ getState }) => {
				return getState().actions;
			},
		getActionFilter:
			() =>
			({ getState }) => {
				return getState().actionFilter;
			},
		getInput:
			() =>
			({ getState }) => {
				return getState().input;
			},
		getActiveAction:
			() =>
			({ getState }) => {
				return getState().activeAction;
			},
		getButtonClickedTimestamp:
			() =>
			({ getState }) => {
				return getState().buttonClickedTimestamp;
			},
	},
});

export const useConversationActionsStore = createHook(store);
export const useConversationActionsStoreActions = createActionsHook(store);
export const useConversationActionsStoreState = createStateHook(store);

export type ConversationActionsStoreActions = ReturnType<typeof useConversationActionsStoreActions>;
